import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { 
  Globe, Users, Dog, Crown, Heart, MapPin, Search, Filter,
  ChevronRight, Languages, Building2, Star, Lightbulb, Map, List
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import MapView from '../components/MapView';

const API = process.env.REACT_APP_BACKEND_URL;

// Country flags and names
const COUNTRY_INFO = {
  US: { name: 'United States', flag: '🇺🇸' },
  ES: { name: 'Spain', flag: '🇪🇸' },
  UK: { name: 'United Kingdom', flag: '🇬🇧' },
  DE: { name: 'Germany', flag: '🇩🇪' },
  FR: { name: 'France', flag: '🇫🇷' },
  IT: { name: 'Italy', flag: '🇮🇹' },
  PT: { name: 'Portugal', flag: '🇵🇹' },
  MX: { name: 'Mexico', flag: '🇲🇽' },
  CA: { name: 'Canada', flag: '🇨🇦' },
  AU: { name: 'Australia', flag: '🇦🇺' },
  BR: { name: 'Brazil', flag: '🇧🇷' },
  JP: { name: 'Japan', flag: '🇯🇵' }
};

const LANGUAGE_LABELS = {
  en: { name: 'English', flag: '🇺🇸' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  pt: { name: 'Português', flag: '🇵🇹' },
  it: { name: 'Italiano', flag: '🇮🇹' }
};

const ClubDirectory = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list');

  // Approximate coordinates for countries
  const COUNTRY_COORDS = {
    US: { lat: 39.8283, lng: -98.5795 },
    ES: { lat: 40.4637, lng: -3.7492 },
    UK: { lat: 55.3781, lng: -3.4360 },
    DE: { lat: 51.1657, lng: 10.4515 },
    FR: { lat: 46.2276, lng: 2.2137 },
    IT: { lat: 41.8719, lng: 12.5674 },
    MX: { lat: 23.6345, lng: -102.5528 },
    CA: { lat: 56.1304, lng: -106.3468 },
    AU: { lat: -25.2744, lng: 133.7751 },
    BR: { lat: -14.2350, lng: -51.9253 },
    JP: { lat: 36.2048, lng: 138.2529 },
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      const response = await fetch(`${API}/api/clubs/`);
      if (response.ok) {
        const data = await response.json();
        setClubs(data);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique countries from clubs
  const availableCountries = [...new Set(clubs.map(c => c.country))].sort();

  // Filter clubs
  const filteredClubs = clubs.filter(club => {
    const matchesSearch = !searchQuery || 
      club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      club.tagline.toLowerCase().includes(searchQuery.toLowerCase()) ||
      club.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCountry = !countryFilter || club.country === countryFilter;
    const matchesType = !typeFilter || club.club_type === typeFilter;
    
    return matchesSearch && matchesCountry && matchesType;
  });

  // Group clubs by country for display
  const clubsByCountry = filteredClubs.reduce((acc, club) => {
    const country = club.country || 'Other';
    if (!acc[country]) acc[country] = [];
    acc[country].push(club);
    return acc;
  }, {});

  const ClubCard = ({ club }) => {
    const countryInfo = COUNTRY_INFO[club.country] || { name: club.country, flag: '🌍' };
    
    return (
      <Link 
        to={`/club/${club.slug}`}
        className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100"
        data-testid={`club-card-${club.slug}`}
      >
        {/* Banner */}
        <div 
          className="h-24 relative"
          style={{ backgroundColor: club.primary_color }}
        >
          {club.banner_url ? (
            <img 
              src={club.banner_url} 
              alt={club.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Dog className="w-12 h-12 text-white/20" />
            </div>
          )}
          
          {/* Club type badge */}
          <div className="absolute top-2 left-2 flex gap-1">
            {club.club_type === 'flagship' && (
              <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                <Crown className="w-3 h-3" /> Official
              </span>
            )}
            {club.club_type === 'ngo' && (
              <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                <Heart className="w-3 h-3" /> NGO
              </span>
            )}
          </div>
          
          {/* Country badge */}
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 bg-black/30 text-white text-xs font-medium rounded-full">
              {countryInfo.flag} {club.country}
            </span>
          </div>
          
          {/* Logo */}
          <div className="absolute -bottom-6 left-4">
            {club.logo_url ? (
              <img 
                src={club.logo_url}
                alt={club.logo_alt || club.name}
                className="w-14 h-14 rounded-lg shadow-md bg-white object-contain p-1 border-2 border-white"
              />
            ) : (
              <div 
                className="w-14 h-14 rounded-lg shadow-md flex items-center justify-center border-2 border-white"
                style={{ backgroundColor: club.secondary_color }}
              >
                <Dog className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="pt-8 px-4 pb-4">
          <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition">
            {club.name}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-1">{club.tagline}</p>
          
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {club.member_count || 0}
              </span>
              {club.supported_languages && club.supported_languages.length > 1 && (
                <span className="flex items-center gap-1">
                  <Languages className="w-3 h-3" />
                  {club.supported_languages.length}
                </span>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition" />
          </div>
        </div>
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Club Directory"
        description="Explore dog clubs in our network. Join regional clubs, breed-specific groups, and special interest communities."
        url="/clubs"
      />
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-6 h-6" />
                <span className="text-blue-200 text-sm font-medium">Very Big Box Network</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold">{t('directory.clubDirectory')}</h1>
              <p className="text-blue-200 mt-2">
                {t('directory.discoverClubs')}
              </p>
            </div>
            
            {/* Stats */}
            <div className="flex gap-6 text-center">
              <div className="bg-white/10 rounded-lg px-6 py-3">
                <div className="text-2xl font-bold">{clubs.length}</div>
                <div className="text-xs text-blue-200">{t('common.clubs')}</div>
              </div>
              <div className="bg-white/10 rounded-lg px-6 py-3">
                <div className="text-2xl font-bold">{availableCountries.length}</div>
                <div className="text-xs text-blue-200">{t('club.country')}</div>
              </div>
              <div className="bg-white/10 rounded-lg px-6 py-3">
                <div className="text-2xl font-bold">
                  {clubs.reduce((sum, c) => sum + (c.member_count || 0), 0)}
                </div>
                <div className="text-xs text-blue-200">{t('common.members')}</div>
              </div>
            </div>
          </div>
          
          {/* Search & Filters */}
          <div className="mt-8 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input 
                placeholder={t('directory.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-blue-200 h-12"
                data-testid="club-search-input"
              />
            </div>
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-12"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              {t('directory.filters')}
              {(countryFilter || typeFilter) && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500 rounded-full text-xs">
                  {[countryFilter, typeFilter].filter(Boolean).length}
                </span>
              )}
            </Button>
            {/* Map/List Toggle */}
            <div className="flex rounded-lg border border-white/20 overflow-hidden">
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none text-white hover:bg-white/20 ${viewMode === 'list' ? 'bg-white/20' : ''}`}
                onClick={() => setViewMode('list')}
                data-testid="clubs-list-view-btn"
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-none text-white hover:bg-white/20 ${viewMode === 'map' ? 'bg-white/20' : ''}`}
                onClick={() => setViewMode('map')}
                data-testid="clubs-map-view-btn"
              >
                <Map className="w-4 h-4 mr-1" />
                Map
              </Button>
            </div>
          </div>
          
          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white/10 rounded-lg flex flex-wrap gap-4">
              <div>
                <label className="block text-xs text-blue-200 mb-1">{t('club.country')}</label>
                <select 
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="bg-white/20 border-white/30 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="country-filter"
                >
                  <option value="">{t('directory.allCountries')}</option>
                  {availableCountries.map(country => (
                    <option key={country} value={country}>
                      {COUNTRY_INFO[country]?.flag} {COUNTRY_INFO[country]?.name || country}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-blue-200 mb-1">{t('directory.allTypes')}</label>
                <select 
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="bg-white/20 border-white/30 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="type-filter"
                >
                  <option value="">{t('directory.allTypes')}</option>
                  <option value="flagship">{t('directory.official')}</option>
                  <option value="standard">Standard</option>
                  <option value="ngo">{t('directory.ngo')}</option>
                </select>
              </div>
              {(countryFilter || typeFilter) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-blue-200 hover:text-white self-end"
                  onClick={() => { setCountryFilter(''); setTypeFilter(''); }}
                >
                  {t('directory.clearFilters')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {filteredClubs.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('directory.noClubsFound')}</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || countryFilter || typeFilter 
                ? t('directory.tryAdjustingFilters')
                : t('directory.requestNewClub')}
            </p>
            <Button onClick={() => navigate('/request-club')}>
              {t('directory.requestNewClub')}
            </Button>
          </div>
        ) : viewMode === 'map' ? (
          /* Map View */
          <div className="mb-10">
            <MapView
              height="500px"
              dataTestId="clubs-map-view"
              zoom={2}
              markers={filteredClubs.map(club => {
                const coords = COUNTRY_COORDS[club.country] || { lat: 0, lng: 0 };
                return {
                  id: club.id,
                  lat: coords.lat + (Math.random() - 0.5) * 2,
                  lng: coords.lng + (Math.random() - 0.5) * 2,
                  title: club.name,
                  color: club.primary_color || '#c9762b',
                  club,
                };
              })}
              renderPopup={(marker) => (
                <div className="min-w-[180px]">
                  <h3 className="font-bold text-sm mb-1">{marker.club.name}</h3>
                  <p className="text-xs text-gray-500 mb-1">{marker.club.tagline}</p>
                  <p className="text-xs text-gray-400 mb-2">
                    {COUNTRY_INFO[marker.club.country]?.flag} {COUNTRY_INFO[marker.club.country]?.name || marker.club.country}
                    {' '}&middot; {marker.club.member_count || 0} members
                  </p>
                  <a
                    href={`/club/${marker.club.slug}`}
                    className="text-xs text-blue-600 font-medium hover:underline"
                  >
                    Visit Club &rarr;
                  </a>
                </div>
              )}
            />
          </div>
        ) : (
          <>
            {/* Featured Clubs */}
            {!searchQuery && !countryFilter && !typeFilter && (
              <section className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  {t('directory.featuredClubs')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {clubs.filter(c => c.is_featured || c.club_type === 'flagship').map(club => (
                    <ClubCard key={club.id} club={club} />
                  ))}
                </div>
              </section>
            )}
            
            {/* All Clubs by Country (exclude featured clubs to avoid duplication) */}
            {Object.entries(
              filteredClubs
                .filter(club => !searchQuery && !countryFilter && !typeFilter ? !(club.is_featured || club.club_type === 'flagship') : true)
                .reduce((acc, club) => {
                  const country = club.country || 'Other';
                  if (!acc[country]) acc[country] = [];
                  acc[country].push(club);
                  return acc;
                }, {})
            ).map(([country, countryClubs]) => (
              <section key={country} className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-2xl">{COUNTRY_INFO[country]?.flag || '🌍'}</span>
                  {COUNTRY_INFO[country]?.name || country}
                  <span className="text-sm font-normal text-gray-500">
                    ({countryClubs.length} {countryClubs.length === 1 ? 'club' : 'clubs'})
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {countryClubs.map(club => (
                    <ClubCard key={club.id} club={club} />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
        
        {/* CTA to create club */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
          <Globe className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {t('directory.dontSeeYourCountry')}
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {t('directory.expandingGlobally')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/suggest-club')}
              className="bg-primary hover:bg-primary/90"
              data-testid="suggest-club-btn"
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              Suggest a Club
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/request-club')}
            >
              <MapPin className="w-4 h-4 mr-2" />
              {t('directory.requestClubForRegion')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubDirectory;
